import { useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import PremiumPageHeader from "../../components/PremiumPageHeader";
import { ListPageFloatingControls } from "../../components/ListPageLayout";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { getAllMoisture, deleteMoisture } from "../../services/MoistureCorrectionServices";
import { displayError } from "../../helpers";

function MoistureCorrectionTable() {
    const InitialData = {
        total_pages: 0,
        records: [],
    };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);
    const right_pinned = ["id"];

    const columns = useMemo(
        () => [
            { fieldName: 'mc_no', headerName: 'MC No', size: 90 },
            { fieldName: 'sl_dt', headerName: 'Date', size: 88 },
            { fieldName: 'sl_time', headerName: 'Time', size: 72 },
            { fieldName: 'plant_name', headerName: 'Plant', size: 90 },
            { fieldName: 'moisture_msand', headerName: 'Moisture Msand', size: 88, align: 'right' },
            { fieldName: 'moisture_csand', headerName: 'Moisture Csand', size: 88, align: 'right' },
            { fieldName: 'moisture_rsand', headerName: 'Moisture Rsand', size: 88, align: 'right' },
            { fieldName: 'moisture_12mm', headerName: 'Moisture 12mm', size: 88, align: 'right' },
            { fieldName: 'moisture_20mm', headerName: 'Moisture 20mm', size: 88, align: 'right' },
            { fieldName: 'absorption_msand', headerName: 'Absorption Msand', size: 98, align: 'right' },
            { fieldName: 'absorption_csand', headerName: 'Absorption Csand', size: 98, align: 'right' },
            { fieldName: 'absorption_rsand', headerName: 'Absorption Rsand', size: 98, align: 'right' },
            { fieldName: 'absorption_12mm', headerName: 'Absorption 12mm', size: 98, align: 'right' },
            { fieldName: 'absorption_20mm', headerName: 'Absorption 20mm', size: 98, align: 'right' },
            { fieldName: 'id', headerName: 'ROW_ACTIONS' },
        ],
        [],
    );

    const onEditMoisturecorr = (moistureId) => {
        navigate(`/MoistureCorrectionEdit/${moistureId}/`);
    };

    const onDeleteMoisturecorr = (moistureId, event) => {
        event.preventDefault();
        Swal.fire({
            title: 'Are you sure to Delete?',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
        }).then((result) => {
            if (!result.isConfirmed) {
                Swal.fire('Not Deleted', '', 'info');
                return;
            }
            setIsLoading(true);
            deleteMoisture(cookies, moistureId)
                .then(() => {
                    Swal.fire('Deleted Successfully!', '', 'success');
                    setRefreshKey((oldKey) => oldKey + 1);
                })
                .catch((err) => {
                    setIsLoading(false);
                    displayError(err.response?.data ?? err.message, 'Delete Failed');
                });
        });
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((c) => c.headerName),
            data.records.map((r) => ({
                mc_no: r.mc_no,
                sl_dt: r.sl_dt,
                sl_time: r.sl_time,
                plant_name: r.plant_name,
                moisture_msand: r.moisture_msand,
                moisture_csand: r.moisture_csand,
                moisture_rsand: r.moisture_rsand,
                moisture_12mm: r.moisture_12mm,
                moisture_20mm: r.moisture_20mm,
                absorption_msand: r.absorption_msand,
                absorption_csand: r.absorption_csand,
                absorption_rsand: r.absorption_rsand,
                absorption_12mm: r.absorption_12mm,
                absorption_20mm: r.absorption_20mm,
            })),
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Moisture Correction',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records.map((r) => ({
                mc_no: r.mc_no,
                sl_dt: r.sl_dt,
                sl_time: r.sl_time,
                plant_name: r.plant_name,
                moisture_msand: r.moisture_msand,
                moisture_csand: r.moisture_csand,
                moisture_rsand: r.moisture_rsand,
                moisture_12mm: r.moisture_12mm,
                moisture_20mm: r.moisture_20mm,
                absorption_msand: r.absorption_msand,
                absorption_csand: r.absorption_csand,
                absorption_rsand: r.absorption_rsand,
                absorption_12mm: r.absorption_12mm,
                absorption_20mm: r.absorption_20mm,
            })),
            'Moisture.pdf',
        );
    };

    useEffect(() => {
        setIsLoading(true);
        getAllMoisture(cookies)
            .then((moistureList) => {
                const rows = moistureList?.moisture_correction_list || [];
                const tableData = rows.map((mc) => ({
                    id: mc.id,
                    mc_no: mc.mc_no || '',
                    sl_dt: mc.sl_dt || '',
                    sl_time: mc.sl_time || '',
                    plant_name: mc.plant_alias || mc.plant_name || '',
                    moisture_msand: parseFloat(mc.moisture?.msand ?? 0),
                    moisture_csand: parseFloat(mc.moisture?.csand ?? 0),
                    moisture_rsand: parseFloat(mc.moisture?.rsand ?? 0),
                    moisture_12mm: parseFloat(mc.moisture?.['12mm'] ?? 0),
                    moisture_20mm: parseFloat(mc.moisture?.['20mm'] ?? 0),
                    absorption_msand: parseFloat(mc.absorption?.msand ?? 0),
                    absorption_csand: parseFloat(mc.absorption?.csand ?? 0),
                    absorption_rsand: parseFloat(mc.absorption?.rsand ?? 0),
                    absorption_12mm: parseFloat(mc.absorption?.['12mm'] ?? 0),
                    absorption_20mm: parseFloat(mc.absorption?.['20mm'] ?? 0),
                }));

                setData({ records: tableData });
                setIsLoading(false);
            })
            .catch((err) => {
                setIsLoading(false);
                displayError(
                    err.response?.data ?? err.message,
                    'Failed to load moisture correction list',
                );
            });
    }, [cookies, refreshKey]);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageFloatingControls
                    formLink="/MoistureCorrection"
                    addNewLabel="New Moisture Correction"
                    homeLink="/Home"
                />
                <div className="container">
                    <PremiumPageHeader
                        module="QUALITY CONTROL"
                        title="List of Moisture Correction"
                        subtitle="View and edit saved moisture correction records for each plant."
                        modeLabel="VIEW MODE"
                    />
                </div>

                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title="LIST OF MOISTURE CORRECTION"
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditMoisturecorr}
                            onRowDelete={onDeleteMoisturecorr}
                            hideDelete={true}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            right_pinned={right_pinned}
                            compactRows={true}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MoistureCorrectionTable;
